import type { FileUIPart, ModelMessage, TextUIPart, ToolUIPart } from 'ai'
import { useCallback, useRef, useState } from 'react'
import type { ConnectorInfo } from '@/lib/agents/agent'
import { createAgent } from '@/lib/agents/agent'
import { uiMessageToModelMessage } from '@/lib/agents/message-utils'
import { clearToolMessageCallback } from '@/lib/agents/tool-ui'
import { gk } from '@/lib/ak'
import type { Memory } from '@/lib/api-handlers/memories'
import { saveAssistantMessage } from '@/lib/chats'
import { devLog } from '@/lib/dev-log'
import {
  buildFinalMessageParts,
  createInitialAssistantMessage,
  normalizeUsage,
} from '@/lib/utils/message-helpers'
import {
  handleAbortMessage,
  setupToolCallback,
  updateAssistantMessageContent,
  updateAssistantMessageFinal,
} from '@/lib/utils/streaming-helpers'

type MessagePart = TextUIPart | FileUIPart | ToolUIPart

interface UseMessageStreamingProps {
  platform: string
  updateMessages: (
    updater: (messages: ModelMessage[]) => ModelMessage[],
    chatId?: string
  ) => void
  connectors?: ConnectorInfo[]
  memories?: Memory[]
  workbookKey?: string | null
}

export function useMessageStreaming({
  platform,
  updateMessages,
  connectors,
  memories,
  workbookKey,
}: UseMessageStreamingProps) {
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null)
  const sendingToChatIdRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const streamChat = useCallback(
    async (
      messages: ModelMessage[],
      model: string,
      chatId: string,
      turnId: string | null,
      abortSignal?: AbortSignal,
      isIncognito: boolean = false
    ): Promise<void> => {
      const isExcel = platform === 'excel'

      let apiKey: string | null = null
      try {
        apiKey = await gk(model)
      } catch (gkError) {
        console.error('[streamChat] API key fetch failed:', gkError)
        throw new Error(
          `${gkError instanceof Error ? gkError.message : String(gkError)}`
        )
      }
      if (!apiKey) {
        console.error('[streamChat] API key is null for model:', model)
        throw new Error('Failed to send chat. Please try again.')
      }

      // Create assistant message for streaming - will accumulate all parts (text + tools)
      const assistantMsg = createInitialAssistantMessage()
      const assistantMsgId = assistantMsg.id
      updateMessages((msgs) => [...msgs, assistantMsg], chatId)

      // Collect tool parts as they come in (instead of creating separate messages)
      const toolParts: MessagePart[] = []
      setupToolCallback(assistantMsgId, toolParts, chatId, updateMessages)

      try {
        // Convert UI messages (with file parts) to model messages
        // This is async because file URLs need to be converted to base64
        // (AI models can't access signed URLs, blob URLs, etc. due to CORS)
        const modelMessages = (
          await Promise.all(messages.map((msg) => uiMessageToModelMessage(msg)))
        ).filter((m): m is ModelMessage => m !== null)

        // Check if aborted before starting stream
        if (abortSignal?.aborted) {
          return
        }

        const agent = createAgent(
          apiKey,
          model,
          isExcel,
          connectors,
          memories,
          workbookKey
        )

        devLog('info', `Starting stream for model: ${model}`)
        let result: Awaited<ReturnType<typeof agent.stream>>
        try {
          result = await agent.stream({
            messages: modelMessages,
            abortSignal,
          })
          devLog('info', 'agent.stream() resolved OK')
        } catch (err) {
          devLog('error', 'agent.stream() threw', err)
          throw err
        }

        // Use manual stream consumption instead of for-await-of
        // WKWebView on Mac has issues with async iteration over streams
        const reader = result.textStream.getReader()
        let chunkCount = 0
        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) {
              devLog('info', `Stream ended after ${chunkCount} chunks`)
              break
            }
            chunkCount++
            if (abortSignal?.aborted) {
              await handleAbortMessage(
                assistantMsgId,
                chatId,
                turnId,
                model,
                result,
                toolParts,
                isIncognito,
                updateMessages
              )
              return
            }
            if (value) {
              updateAssistantMessageContent(
                assistantMsgId,
                value,
                chatId,
                updateMessages
              )
            }
          }
        } finally {
          reader.releaseLock()
        }

        // Check if aborted before getting final text
        if (abortSignal?.aborted) {
          await handleAbortMessage(
            assistantMsgId,
            chatId,
            turnId,
            model,
            result,
            toolParts,
            isIncognito,
            updateMessages
          )
          return
        }

        // Get the final complete text from the SDK (more reliable than concatenating chunks)
        let finalText: string
        try {
          finalText = await result.text
          devLog('info', `Final text length: ${finalText?.length ?? 0}`)
        } catch (textErr) {
          devLog('error', 'result.text threw', textErr)
          throw textErr
        }

        // Check if aborted before finalizing
        if (abortSignal?.aborted) {
          await handleAbortMessage(
            assistantMsgId,
            chatId,
            turnId,
            model,
            result,
            toolParts,
            isIncognito,
            updateMessages
          )
          return
        }

        // Build final assistant message with all parts: text + tool parts
        const finalParts = buildFinalMessageParts(finalText, toolParts)

        // Update UI with final consolidated message
        updateAssistantMessageFinal(
          assistantMsgId,
          finalText,
          finalParts,
          chatId,
          updateMessages
        )

        // Save consolidated assistant message to backend (one message with all parts)
        if (!isIncognito && turnId && chatId && !abortSignal?.aborted) {
          const finalAssistantMsg = {
            id: assistantMsgId,
            role: 'assistant' as const,
            content: finalText,
            ...(finalParts.length > 0 && { parts: finalParts }),
          } as ModelMessage & { id: string }
          try {
            const rawUsage = await result.totalUsage
            await saveAssistantMessage({
              chatId,
              turnId,
              message: finalAssistantMsg,
              usage: normalizeUsage(rawUsage),
              model,
            })
          } catch (error) {
            console.error('Failed to save assistant message to backend', error)
            // Continue anyway - non-blocking
          }
        }
      } finally {
        clearToolMessageCallback()
      }
    },
    [platform, updateMessages, connectors, memories, workbookKey]
  )

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setLoadingChatId(null)
      sendingToChatIdRef.current = null
    }
  }, [])

  return {
    loadingChatId,
    setLoadingChatId,
    sendingToChatIdRef,
    abortControllerRef,
    streamChat,
    handleStop,
  }
}
